import { Modal, Form, Input, Avatar, Typography, Divider, Tag, message, Row, Col, Button } from 'antd';
import { LockOutlined, UserOutlined, MailOutlined, PhoneOutlined, CameraOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { authApi } from '../../api/auth.api';
import { useAuthStore } from '../../store/auth.store';
import { resolveFileUrl } from '../../utils/helpers';

const { Text, Title } = Typography;

const ROLE_LABEL = {
  admin: 'Administrator',
  school_staff: 'School Staff',
  doctor: 'Doctor',
};

const ROLE_COLOR = {
  admin: 'blue',
  school_staff: 'green',
  doctor: 'purple',
};

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_SIZE_MB = 5;

const ProfileModal = ({ open, onClose }) => {
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const fileInputRef = useRef(null);
  const { user, setUser } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (open && user) {
      profileForm.setFieldsValue({ name: user.name, email: user.email });
      setAvatarFile(null);
      setAvatarPreview('');
    }
  }, [open, user, profileForm]);

  const handleAvatarPick = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) { return; }
    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error('Only JPG, JPEG, and PNG image files are allowed.');
      return;
    }
    if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
      message.error(`Image must be smaller than ${MAX_SIZE_MB}MB`);
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSaveProfile = async () => {
    const values = await profileForm.validateFields();
    setProfileLoading(true);
    try {
      let payload;
      if (avatarFile) {
        payload = new FormData();
        if (values.name) { payload.append('name', values.name); }
        if (isAdmin && values.email) { payload.append('email', values.email); }
        payload.append('avatar', avatarFile);
      } else {
        payload = { name: values.name };
        if (isAdmin) { payload.email = values.email; }
      }
      const updated = await authApi.updateProfile(payload);
      setUser(updated.data ?? updated);
      setAvatarFile(null);
      setAvatarPreview('');
      message.success('Profile updated successfully');
    } catch (err) {
      message.error(err.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async () => {
    const values = await passwordForm.validateFields();
    setPasswordLoading(true);
    try {
      await authApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success('Password changed successfully. Please log in again with the new password.');
      passwordForm.resetFields();
    } catch (err) {
      message.error(err.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleClose = () => {
    profileForm.resetFields();
    passwordForm.resetFields();
    setAvatarFile(null);
    setAvatarPreview('');
    onClose();
  };

  const avatarSrc = avatarPreview || resolveFileUrl(user?.avatar);

  return (
    <Modal
      title="My Profile"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      destroyOnClose
    >
      {/* User Info Section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0 20px' }}>
        <div
          style={{ position: 'relative', width: 64, height: 64, flexShrink: 0, cursor: 'pointer' }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleAvatarPick}
          />
          <Avatar size={64} src={avatarSrc || undefined} style={{ background: 'var(--wl-primary-gradient)', fontSize: 26 }}>
            {!avatarSrc && user?.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div
            style={{
              position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = 0; }}
          >
            <CameraOutlined style={{ color: '#fff', fontSize: 18 }} />
          </div>
        </div>
        <div>
          <Title level={5} style={{ margin: 0 }}>{user?.name}</Title>
          <Tag color={ROLE_COLOR[user?.role]} style={{ marginTop: 4 }}>
            {ROLE_LABEL[user?.role]}
          </Tag>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <MailOutlined style={{ marginRight: 6 }} />{user?.email}
            </Text>
            {user?.phone && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <PhoneOutlined style={{ marginRight: 6 }} />{user?.phone}
              </Text>
            )}
          </div>
        </div>
      </div>

      <Divider style={{ margin: '0 0 16px' }}>
        <UserOutlined /> Profile Information
      </Divider>

      <Form form={profileForm} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Enter your name' }]}
        >
          <Input placeholder="Enter your name" />
        </Form.Item>

        {isAdmin && (
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Enter your email' },
              { type: 'email', message: 'Enter a valid email address' },
            ]}
          >
            <Input placeholder="Enter your email" />
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button type="primary" loading={profileLoading} onClick={handleSaveProfile}>
            Save Changes
          </Button>
        </Form.Item>
      </Form>

      <Divider style={{ margin: '24px 0 16px' }}>
        <LockOutlined /> Change Password
      </Divider>

      <Form form={passwordForm} layout="vertical">
        <Form.Item
          name="currentPassword"
          label="Current Password"
          rules={[{ required: true, message: 'Enter your current password' }]}
        >
          <Input.Password placeholder="Enter current password" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[
                { required: true, message: 'Enter new password' },
                { min: 8, message: 'At least 8 characters' },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
                  message: 'Uppercase, lowercase, number & special char required',
                },
              ]}
            >
              <Input.Password placeholder="New password" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="confirmNewPassword"
              label="Confirm New Password"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm password" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button type="primary" loading={passwordLoading} onClick={handleChangePassword}>
            Update Password
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ProfileModal;
